import { LaqorrFileEntry } from 'src/fileUtils';
import { ExecutionCompleteArgs, ExecutionId } from './displayHandler';
import { LocalTimeZoneClock } from 'src/clock';

// A wrapper around the AVPlayAPI
export interface ITizenVideoDisplayHandlerContext {
  playerPrepare(mediaFile: LaqorrFileEntry) : PromiseLike<void>;
  // Render one of the first frames. 
  // (This is so, when you transition from image to video, 
  // you do not see the final frame of the last played video)
  playerInitialRender(mediaFile: LaqorrFileEntry): Promise<void>;
  playerPlay();
  playerStop() : Promise<void>;
  hide() : Promise<void>;
  firePlayComplete(executionCompleteArgs: ExecutionCompleteArgs);
  log(logText: string);

  requestVideoPlayerReservation(): number;
  relinquishVideoPlayerReservation();
  cancelVideoPlayerReservationRequest(requestId: number);
  localTimeZoneClock: LocalTimeZoneClock;
}

export interface ITizenVideoDisplayHandlerState {
    onVideoPlayerReserved(reservationId: number): Promise<ITizenVideoDisplayHandlerState>;
    onPlayRequested(mediaFile: LaqorrFileEntry, executionId: ExecutionId) : Promise<ITizenVideoDisplayHandlerState>;
    onStopAllRequested() : Promise<ITizenVideoDisplayHandlerState>;
    onStreamCompleted() : Promise<ITizenVideoDisplayHandlerState>;
    onPrepareRequested(mediaFile: LaqorrFileEntry): Promise<ITizenVideoDisplayHandlerState>;
    onPrepareCancelled() : Promise<ITizenVideoDisplayHandlerState>;
    onError(): Promise<ITizenVideoDisplayHandlerState>;
  }
  

export class TizenVideoDisplayHandlerStateIdle implements ITizenVideoDisplayHandlerState {
    constructor(readonly context: ITizenVideoDisplayHandlerContext) {
    }

    async onPlayRequested(mediaFile: LaqorrFileEntry, executionId: ExecutionId) : Promise<ITizenVideoDisplayHandlerState> {
        const reservationRequestId = this.context.requestVideoPlayerReservation();
        const newState = new TizenVideoDisplayHandlerStateRequestingReservationForPlay(
            this.context,
            reservationRequestId,
            mediaFile,
            executionId
        );
        return Promise.resolve<ITizenVideoDisplayHandlerState>(newState);
    }
  
    onStreamCompleted() {
      this.context.log("TizenVideoDisplayHandlerStateIdle.onStreamCompleted unexpectedly invoked");
      return Promise.resolve<ITizenVideoDisplayHandlerState>(this);
    }
  
    async onPrepareRequested(mediaFile: LaqorrFileEntry) : Promise<ITizenVideoDisplayHandlerState> {
      
      this.context.log("TizenVideoStateIdle.onPrepareRequested invoked");
      const reservationId = this.context.requestVideoPlayerReservation();
      const newState = new TizenVideoDisplayHandlerStateRequestingReservationForPrepare(this.context, reservationId, mediaFile);
      return Promise.resolve<ITizenVideoDisplayHandlerState>(newState);
  
    }
  
    onError(): Promise<ITizenVideoDisplayHandlerState> {
      this.context.log('TizenVideoDisplayHandlerStateIdle.onError invoked');
      return Promise.resolve<ITizenVideoDisplayHandlerState>(this);   
    }
  
    onStopAllRequested() : Promise<ITizenVideoDisplayHandlerState> {
      this.context.log("TizenVideoDisplayHandlerStateIdle.onStopAllRequested  invoked");
      return Promise.resolve<ITizenVideoDisplayHandlerState>(this);  
    }
  
    onVideoPlayerReserved(reservationId: number): Promise<ITizenVideoDisplayHandlerState> {
        this.context.log("TizenVideoDisplayHandlerStateIdle.onVideoPlayerReserved unexpectedly invoked.");
        this.context.relinquishVideoPlayerReservation();
        return Promise.resolve<ITizenVideoDisplayHandlerState>(this); 
    }

    onPrepareCancelled() {
        this.context.log("TizenVideoDisplayHandlerStateIdle.onPrepareCancelled unexpectedly invoked.");
        return Promise.resolve<ITizenVideoDisplayHandlerState>(this);
    }
  }

class TizenVideoDisplayHandlerStatePrepared implements ITizenVideoDisplayHandlerState {
    constructor(readonly context: ITizenVideoDisplayHandlerContext, readonly mediaFile: LaqorrFileEntry) {
    }

    onVideoPlayerReserved(reservationId: number): Promise<ITizenVideoDisplayHandlerState> {
        this.context.log("TizenVideoDisplayHandlerStatePrepared.onVideoPlayerReserved unexpectedly invoked");
        return Promise.resolve<ITizenVideoDisplayHandlerState>(this);
    }
  
    onStreamCompleted() {
      this.context.log("TizenVideoDisplayHandlerStatePrepared.onStreamCompleted unexpectedly invoked");
      return Promise.resolve<ITizenVideoDisplayHandlerState>(this);
    }
  
    async onError(): Promise<ITizenVideoDisplayHandlerState> {
      this.context.log('TizenVideoDisplayHandlerStatePrepared.onError invoked');
      await this.context.playerStop();
      this.context.hide();
      return new TizenVideoDisplayHandlerStateIdle(this.context);   
    }
  
    onStopAllRequested() : Promise<ITizenVideoDisplayHandlerState> {
      this.context.log("TizenVideoDisplayHandlerStatePrepared.onStopAllRequested invoked");
      this.context.relinquishVideoPlayerReservation();
      return Promise.resolve<TizenVideoDisplayHandlerStateIdle>(new TizenVideoDisplayHandlerStateIdle(this.context));   
    }
  
    async onPrepareRequested(mediaFile: LaqorrFileEntry) : Promise<ITizenVideoDisplayHandlerState> {
      await this.context.playerPrepare(mediaFile);
      return new TizenVideoDisplayHandlerStatePrepared(this.context, mediaFile);  
    }
  
    async onPlayRequested(mediaFile: LaqorrFileEntry, executionId: ExecutionId) : Promise<ITizenVideoDisplayHandlerState> {
      const startDate = this.context.localTimeZoneClock.now;
      if(mediaFile.name !== this.mediaFile.name) {
        this.context.log('TizenVideoDisplayHandlerStatePrepared.onPlayRequested. They do not match, so calling playerPrepare again');
        await this.context.playerPrepare(mediaFile);
      }
      this.context.playerPlay();
      return Promise.resolve<ITizenVideoDisplayHandlerState>(
        new TizenVideoDisplayHandlerStatePlaying(
          this.context,
          mediaFile,
          executionId, 
          startDate
        )
      );
    }
    async onPrepareCancelled(): Promise<ITizenVideoDisplayHandlerState> {
      await this.context.hide();
      this.context.relinquishVideoPlayerReservation();
      return new TizenVideoDisplayHandlerStateIdle(this.context);
    }
  }


class TizenVideoDisplayHandlerStateRequestingReservationForPrepare implements ITizenVideoDisplayHandlerState {
    constructor(readonly context: ITizenVideoDisplayHandlerContext, readonly reservationRequestId: number, readonly mediaFile: LaqorrFileEntry) {
    }

    async onVideoPlayerReserved(reservationRequestId: number): Promise<ITizenVideoDisplayHandlerState> {
        if (reservationRequestId !== this.reservationRequestId) {
            this.context.log("TizenVideoDisplayHandlerStateRequestingReservationForPrepare mismatch in reservation id. Handling this anyway")
        }
        await this.context.playerInitialRender(this.mediaFile);
        await this.context.playerPrepare(this.mediaFile);
        return new TizenVideoDisplayHandlerStatePrepared(this.context, this.mediaFile);
    }

    onError(): Promise<ITizenVideoDisplayHandlerState> {
        this.context.log("TizenVideoDisplayHandlerStateRequestingReservationForPrepare onError fired");
        return Promise.resolve<ITizenVideoDisplayHandlerState>(this);
    }

    onPlayRequested(mediaFile: LaqorrFileEntry, executionId: ExecutionId): Promise<ITizenVideoDisplayHandlerState> {
        const newState = new TizenVideoDisplayHandlerStateRequestingReservationForPlay(
            this.context,
            this.reservationRequestId,
            mediaFile, // Whether this matches the original media file or not is irrelevant
            executionId
        );
        return Promise.resolve<ITizenVideoDisplayHandlerState>(newState);
    }

    onPrepareRequested(mediaFile: LaqorrFileEntry): Promise<ITizenVideoDisplayHandlerState> {
        this.context.log("TizenVideoDisplayHandlerStateRequestingReservationForPrepare onPrepareRequested fired");
        const newState = new TizenVideoDisplayHandlerStateRequestingReservationForPrepare(
            this.context,
            this.reservationRequestId,
            mediaFile
        );
        return Promise.resolve<ITizenVideoDisplayHandlerState>(newState);
    }

    async onPrepareCancelled(): Promise<ITizenVideoDisplayHandlerState> {
      this.context.cancelVideoPlayerReservationRequest(this.reservationRequestId);
      return new TizenVideoDisplayHandlerStateIdle(this.context);
    }

    onStopAllRequested(): Promise<ITizenVideoDisplayHandlerState> {
        this.context.log("TizenVideoDisplayHandlerStateRequestingReservationForPrepare onStopAllRequested fired");
        this.context.cancelVideoPlayerReservationRequest(this.reservationRequestId);
        return Promise.resolve<ITizenVideoDisplayHandlerState>(new TizenVideoDisplayHandlerStateIdle(this.context));
    }

    onStreamCompleted(): Promise<ITizenVideoDisplayHandlerState> {
        this.context.log("TizenVideoDisplayHandlerStateRequestingReservationForPrepare onStreamCompleted fired. This was unexpected");
        return Promise.resolve<ITizenVideoDisplayHandlerState>(this);
    }
}


  class TizenVideoDisplayHandlerStateRequestingReservationForPlay implements ITizenVideoDisplayHandlerState {
    constructor(
      readonly context : ITizenVideoDisplayHandlerContext,
      readonly reservationRequestId: number,
      readonly mediaFile: LaqorrFileEntry,
      readonly executionId: ExecutionId
    ) {
    }
    async onVideoPlayerReserved(reservationId: number): Promise<ITizenVideoDisplayHandlerState> {
      await this.context.playerPrepare(this.mediaFile);
      this.context.playerPlay();
      const startDate = this.context.localTimeZoneClock.now;
      return new TizenVideoDisplayHandlerStatePlaying(
        this.context,
        this.mediaFile,
        this.executionId,
        startDate
     );
    }

    onError(): Promise<ITizenVideoDisplayHandlerState> {
        return Promise.resolve<ITizenVideoDisplayHandlerState>(this);
    }

    onPlayRequested(mediaFile: LaqorrFileEntry, executionId: ExecutionId): Promise<ITizenVideoDisplayHandlerState> {
        this.context.log("TizenVideoDisplayHandlerStateRequestingReservationForPlay.onPlayRequested invoked. This was unexpected");
        const newInstance = new TizenVideoDisplayHandlerStateRequestingReservationForPlay(
            this.context,
            this.reservationRequestId,
            mediaFile,
            executionId
        );
        return Promise.resolve<ITizenVideoDisplayHandlerState>(newInstance);
    }

    onPrepareRequested(mediaFile: LaqorrFileEntry): Promise<ITizenVideoDisplayHandlerState> {
        this.context.log("TizenVideoDisplayHandlerStateRequestingReservationForPlay.onPrepareRequested invoked.");
        const newInstance = new TizenVideoDisplayHandlerStateRequestingReservationForPlayFilesQueued(
            this.context,
            this.reservationRequestId,
            this.mediaFile,
            this.executionId,
            mediaFile
        );
        return Promise.resolve<ITizenVideoDisplayHandlerState>(newInstance);
    }

    onPrepareCancelled(): Promise<ITizenVideoDisplayHandlerState> {
      return Promise.resolve(this)
    }

    onStopAllRequested(): Promise<ITizenVideoDisplayHandlerState> {
        this.context.log("TizenVideoDisplayHandlerStateRequestingReservationForPlay.onStopAllRequested invoked.");
        this.context.cancelVideoPlayerReservationRequest(this.reservationRequestId);
        return Promise.resolve<ITizenVideoDisplayHandlerState>(new TizenVideoDisplayHandlerStateIdle(this.context));
    }

    onStreamCompleted(): Promise<ITizenVideoDisplayHandlerState> {
        this.context.log("TizenVideoDisplayHandlerStateRequestingReservationForPlay.onPlayRequested invoked. This was unexpected");
        return Promise.resolve<ITizenVideoDisplayHandlerState>(this);
    }
  }

  class TizenVideoDisplayHandlerStateRequestingReservationForPlayFilesQueued implements ITizenVideoDisplayHandlerState {
    constructor(
        readonly context : ITizenVideoDisplayHandlerContext,
        readonly reservationRequestId: number,
        readonly mediaFile: LaqorrFileEntry,
        readonly executionId: ExecutionId,
        readonly mediaFileToPrepare: LaqorrFileEntry
    ) {
    }

    onError(): Promise<ITizenVideoDisplayHandlerState> {
        this.context.log(`TizenVideoDisplayHandlerStateRequestingReservationForPlayFilesQueued.onError invoked`);
        return Promise.resolve<ITizenVideoDisplayHandlerState>(this);
    }

    onPlayRequested(mediaFile: LaqorrFileEntry, executionId: ExecutionId): Promise<ITizenVideoDisplayHandlerState> {
        this.context.log(`TizenVideoDisplayHandlerStateRequestingReservationForPlayFilesQueued.onPlayRequested invoked. This is unexpected`);
        const newState = new TizenVideoDisplayHandlerStateRequestingReservationForPlay(
            this.context,
            this.reservationRequestId,
            mediaFile,
            executionId
        );
        return Promise.resolve<ITizenVideoDisplayHandlerState>(newState);
    }

    onPrepareRequested(mediaFile: LaqorrFileEntry): Promise<ITizenVideoDisplayHandlerState> {
        this.context.log(`TizenVideoDisplayHandlerStateRequestingReservationForPlayFilesQueued.onPrepareRequested invoked.`);
        const newState = new TizenVideoDisplayHandlerStateRequestingReservationForPlayFilesQueued(
            this.context,
            this.reservationRequestId,
            this.mediaFile,
            this.executionId,
            mediaFile
        );
        return Promise.resolve<ITizenVideoDisplayHandlerState>(newState);
    }

    onPrepareCancelled(): Promise<ITizenVideoDisplayHandlerState> {
        this.context.log(`TizenVideoDisplayHandlerStateRequestingReservationForPlayFilesQueued.onPrepareCancelled invoked.`);
        const newState = new TizenVideoDisplayHandlerStateRequestingReservationForPlay(
          this.context,
          this.reservationRequestId,
          this.mediaFile,
          this.executionId
        );
        return Promise.resolve<ITizenVideoDisplayHandlerState>(newState);
    }

    onStopAllRequested(): Promise<ITizenVideoDisplayHandlerState> {
        this.context.log(`TizenVideoDisplayHandlerStateRequestingReservationForPlayFilesQueued.onStopAllRequested invoked. Going to cancel reservation request ${this.reservationRequestId}`);
        this.context.cancelVideoPlayerReservationRequest(this.reservationRequestId);
        return Promise.resolve<ITizenVideoDisplayHandlerState>(new TizenVideoDisplayHandlerStateIdle(this.context));
    }

    onStreamCompleted(): Promise<ITizenVideoDisplayHandlerState> {
        this.context.log(`TizenVideoDisplayHandlerStateRequestingReservationForPlayFilesQueued.onStreamCompleted invoked. Unexpected`);
        return Promise.resolve<ITizenVideoDisplayHandlerState>(this);
    }

    async onVideoPlayerReserved(reservationId: number): Promise<ITizenVideoDisplayHandlerState> {
        await this.context.playerPrepare(this.mediaFile);
        this.context.playerPlay();
        const startDate = this.context.localTimeZoneClock.now;
        return new TizenVideoDisplayHandlerPlayingWithFileQueued(
          this.context,
          this.mediaFile,
          this.executionId,
          startDate,
          this.mediaFileToPrepare
       );
    }
  }

  class TizenVideoDisplayHandlerStatePlaying implements ITizenVideoDisplayHandlerState {
    constructor(
      readonly context: ITizenVideoDisplayHandlerContext, 
      readonly mediaFile: LaqorrFileEntry, 
      readonly executionId: ExecutionId, 
      readonly startTime: Date
    ) {
    }
    async onStreamCompleted() : Promise<ITizenVideoDisplayHandlerState> {
      this.context.log('onStreamCompleted. Invoking playerStop()');
      await this.context.playerStop();
      this.context.log('onStreamCompleted. Invoking hide()');
      this.context.hide();
      this.context.firePlayComplete({
        executionId : this.executionId,
        mediaFile: this.mediaFile,
        startTime: this.startTime,
        succeeded: true
      });
      this.context.relinquishVideoPlayerReservation();
      return new TizenVideoDisplayHandlerStateIdle(this.context);
    }
  
    async onError(): Promise<ITizenVideoDisplayHandlerState> {
      this.context.log('TizenVideoDisplayHandlerStatePlaying.onError invoked. Invoking playerStop');
      try {
        await this.context.playerStop();
        this.context.hide();  
      } catch {
      } finally {
        this.context.firePlayComplete({
          executionId : this.executionId,
          mediaFile: this.mediaFile,
          startTime: this.startTime,
          succeeded: false
        });
      }
      return new TizenVideoDisplayHandlerStateIdle(this.context);
    }

    onVideoPlayerReserved(reservationId: number): Promise<ITizenVideoDisplayHandlerState> {
        this.context.log('TizenVideoDisplayHandlerStatePlaying.onVideoPlayerReserved fired. This is unexpected as the video player should already be reserved');
        return Promise.resolve<ITizenVideoDisplayHandlerState>(this);
    }
  
    async onStopAllRequested() : Promise<ITizenVideoDisplayHandlerState> {
      this.context.log("TizenVideoDisplayHandlerStatePlaying.onStopAllRequested invoked. Invoking playerStop");
      await this.context.playerStop();
      this.context.hide();
      this.context.relinquishVideoPlayerReservation();
      return new TizenVideoDisplayHandlerStateIdle(this.context);
    }
  
    onPlayRequested(mediaFile: LaqorrFileEntry, executionId: ExecutionId) : Promise<ITizenVideoDisplayHandlerState> {
      // Should not happen!
      this.context.log('TizenVideoDisplayHandlerStatePlaying.onPlayRequested invoked. This is unexpected');
      return Promise.resolve<ITizenVideoDisplayHandlerState>(this);  
    }
  
    onPrepareRequested(mediaFile: LaqorrFileEntry): Promise<ITizenVideoDisplayHandlerState> {
      return Promise.resolve<ITizenVideoDisplayHandlerState>(
        new TizenVideoDisplayHandlerPlayingWithFileQueued(
          this.context,
          this.mediaFile,
          this.executionId,
          this.startTime,
          mediaFile
        )
      );
    }

    onPrepareCancelled(): Promise<ITizenVideoDisplayHandlerState> {
      this.context.log('TizenVideoDisplayHandlerStatePlaying - onPrepareCancelled invoked. (Unexpected)');
      return Promise.resolve<ITizenVideoDisplayHandlerState>(this);
    }
  }


class TizenVideoDisplayHandlerPlayingWithFileQueued implements ITizenVideoDisplayHandlerState {
    constructor(
      readonly context: ITizenVideoDisplayHandlerContext,
      readonly mediaFile: LaqorrFileEntry,
      readonly executionId: ExecutionId,
      readonly startTime: Date,
      readonly nextMediaFile: LaqorrFileEntry
    ) {
    }

    onVideoPlayerReserved(reservationId: number): Promise<ITizenVideoDisplayHandlerState> {
        // Should not happen!
        this.context.log('TizenVideoDisplayHandlerPlayingWithFileQueued.onPlayRequested invoked. This is unexpected');
        return Promise.resolve<ITizenVideoDisplayHandlerState>(this);  
    }

    async onStreamCompleted() : Promise<ITizenVideoDisplayHandlerState> {
      this.context.log("TizenVideoDisplayHandlerPlayingWithFileQueued.onStreamCompleted invoked. Invoking playerStop");
      await this.context.playerStop();
      this.context.firePlayComplete({
        executionId : this.executionId,
        mediaFile: this.mediaFile,
        startTime: this.startTime,
        succeeded: true
      });
      await this.context.playerPrepare(this.nextMediaFile);
      return new TizenVideoDisplayHandlerStatePrepared(this.context, this.nextMediaFile);
    }
    async onError(): Promise<ITizenVideoDisplayHandlerState> {
      this.context.log('TizenVideoDisplayHandlerPlayingWithFileQueued.onError invoked. Invoking playerStop');
      try {
        await this.context.playerStop();
      } catch {
      } finally {
        this.context.firePlayComplete({
          executionId : this.executionId,
          mediaFile: this.mediaFile,
          startTime: this.startTime,
          succeeded: false
        });
      }
      await this.context.playerPrepare(this.nextMediaFile);
      return new TizenVideoDisplayHandlerStatePrepared(this.context, this.nextMediaFile);
    }
  
    async onStopAllRequested() : Promise<ITizenVideoDisplayHandlerState> {
      this.context.log("TizenVideoDisplayHandlerPlayingWithFileQueued.onStopAllRequested invoked. Invoking playerStop");
      await this.context.playerStop();
      this.context.hide();
      this.context.relinquishVideoPlayerReservation();
      return new TizenVideoDisplayHandlerStateIdle(this.context);
    }
  
    onPlayRequested(mediaFile: LaqorrFileEntry, executionId: ExecutionId) : Promise<ITizenVideoDisplayHandlerState> {
      // Should not happen!
      this.context.log('TizenVideoDisplayHandlerPlayingWithFileQueued.onPlayRequested invoked');
      return Promise.resolve<ITizenVideoDisplayHandlerState>(this);  
    }
  
    onPrepareRequested(mediaFile: LaqorrFileEntry): Promise<ITizenVideoDisplayHandlerState> {
      // It would be unexpected for this to actually happen
      this.context.log('TizenVideoDisplayHandlerPlayingWithFileQueued - onPrepareRequested invoked');
      return Promise.resolve<ITizenVideoDisplayHandlerState>(
        new TizenVideoDisplayHandlerPlayingWithFileQueued(
          this.context,
          this.mediaFile,
          this.executionId,
          this.startTime,
          mediaFile
        )
      );
    }

    onPrepareCancelled(): Promise<ITizenVideoDisplayHandlerState> {
      this.context.log('TizenVideoDisplayHandlerPlayingWithFileQueued - onPrepareCancelled invoked');
      return Promise.resolve<ITizenVideoDisplayHandlerState>(
        new TizenVideoDisplayHandlerStatePlaying(
          this.context,
          this.mediaFile,
          this.executionId,
          this.startTime
        )
      );
    }
  }